import React, { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  DataPagination,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
  Skeleton,
  Text,
} from '@musa.tech/design-system';
import { useOnDemandRequests } from '@/hooks/useAgreementsOnDemand';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { maskGeneratorName } from '@/utils/demoMode';
import {
  PERIOD_LABELS,
  REQUEST_STATUS_DISPLAY,
  formatAddress,
  formatDay,
  generatorName,
  haulerName,
} from '@/utils/onDemandRequestRules';
import { OnDemandFilterBar } from './OnDemandFilterBar';
import {
  EMPTY_ON_DEMAND_LIST_FILTERS,
  hasAppliedFilters,
  toRequestsFilter,
  type OnDemandListFilters,
} from './onDemandListFilters';

export const REQUESTS_PAGE_SIZE = 25;

/**
 * "Minhas solicitações" (CA-25): status atual, data pedida e transportador. Sem ação e sem
 * motivo da recusa — o serviço não devolve o motivo ao gerador.
 *
 * Mostra só o que foi pedido no fluxo novo: as solicitações anteriores ao corte do roteamento
 * ficam onde estavam e não são migradas nesta entrega.
 */
export const MinhasSolicitacoesTab: React.FC = () => {
  const [filters, setFilters] = useState<OnDemandListFilters>(EMPTY_ON_DEMAND_LIST_FILTERS);
  const [page, setPage] = useState(1);
  const { isDemoMode } = useDemoMode();

  const { data, isLoading, isError } = useOnDemandRequests({
    ...toRequestsFilter(filters),
    page,
    pageSize: REQUESTS_PAGE_SIZE,
  });
  const filtered = hasAppliedFilters(filters);

  return (
    <div className="flex flex-col gap-4">
      <OnDemandFilterBar
        service="collect"
        filters={filters}
        onChange={(next) => {
          setFilters(next);
          setPage(1);
        }}
      />

      {isLoading ? (
        <div className="flex flex-col gap-2" data-testid="requests-loading">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertTitle>Não foi possível carregar as solicitações</AlertTitle>
          <AlertDescription>
            O serviço de coletas está indisponível no momento. Tente novamente em alguns minutos.
          </AlertDescription>
        </Alert>
      ) : !data?.items.length ? (
        <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed border-neutral-200 px-6 py-10 text-center">
          <Text variant="label" size="medium" className="text-neutral-900">
            {filtered ? 'Nenhuma solicitação com esses filtros' : 'Nenhuma solicitação sob demanda ainda'}
          </Text>
          <Text variant="body" size="small" className="max-w-md text-neutral-500">
            {filtered
              ? 'Troque ou limpe os filtros para ver as outras solicitações.'
              : 'As solicitações que você criar aparecem aqui, com o status da resposta do transportador.'}
          </Text>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border bg-white">
            <DataTable>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead>Data</DataTableHead>
                  <DataTableHead>Estabelecimento</DataTableHead>
                  <DataTableHead>Resíduo</DataTableHead>
                  <DataTableHead>Quantidade</DataTableHead>
                  <DataTableHead>Transportador</DataTableHead>
                  <DataTableHead>Status</DataTableHead>
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {data.items.map((request) => {
                  const display = REQUEST_STATUS_DISPLAY[request.status];

                  return (
                    <DataTableRow key={request.id} data-testid={`request-row-${request.id}`}>
                      <DataTableCell>
                        <Text variant="body" size="small" className="text-neutral-900">
                          {formatDay(request.plannedDate)}
                        </Text>
                        {/* turno só existe em solicitação antiga ou criada pelo Hub */}
                        {request.period != null && (
                          <Text variant="body" size="small" className="text-neutral-500">
                            {PERIOD_LABELS[request.period]}
                          </Text>
                        )}
                      </DataTableCell>
                      <DataTableCell>
                        <Text variant="body" size="small" className="text-neutral-900">
                          {maskGeneratorName(generatorName(request.generator), isDemoMode)}
                        </Text>
                        <Text variant="body" size="small" className="text-neutral-500">
                          {formatAddress(request.address)}
                        </Text>
                      </DataTableCell>
                      <DataTableCell>
                        <Text variant="body" size="small" className="text-neutral-900">
                          {request.material.name ?? '—'}
                        </Text>
                        <Text variant="body" size="small" className="text-neutral-500">
                          {request.packaging.name ?? '—'}
                        </Text>
                      </DataTableCell>
                      <DataTableCell>{request.quantity}</DataTableCell>
                      <DataTableCell>{haulerName(request.hauler)}</DataTableCell>
                      <DataTableCell>
                        <Badge variant={display?.variant ?? 'secondary'} className="whitespace-nowrap">{display?.label ?? '—'}</Badge>
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </DataTableBody>
            </DataTable>
          </div>

          {data.total > REQUESTS_PAGE_SIZE && (
            <DataPagination page={page} pageSize={REQUESTS_PAGE_SIZE} total={data.total} onChange={setPage} />
          )}
        </>
      )}
    </div>
  );
};
